// Analysis History Snapshot Store
// Separate IndexedDB for full analysis snapshots with LZ-String compression
// Allows reopening past analyses with full data (charts, commits, diffs, everything)

import { openDB, type IDBPDatabase } from 'idb';
import { compress as poolCompress, decompress as poolDecompress } from './compression-pool';

// ==================== Types ====================

export type AnalysisMode = 'combined' | 'cross_project' | 'jira' | 'linear' | 'trello' | 'git' | 'confluence' | 'slack' | 'teams';

export const ANALYSIS_MODE_LABELS: Record<AnalysisMode, string> = {
  combined: 'Single Repo Analysis',
  cross_project: 'Cross-Project',
  jira: 'Jira Activity',
  linear: 'Linear Activity',
  trello: 'Trello Activity',
  git: 'Git Analysis',
  confluence: 'Confluence Activity',
  slack: 'Slack Monitoring',
  teams: 'Teams Monitoring',
};

export const ANALYSIS_MODE_COLORS: Record<AnalysisMode, { light: string; dark: string }> = {
  combined: { light: 'bg-orange-100 text-orange-700 border-orange-200', dark: 'bg-orange-500/20 text-orange-300 border-orange-500/30' },
  cross_project: { light: 'bg-emerald-100 text-emerald-700 border-emerald-200', dark: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30' },
  jira: { light: 'bg-blue-100 text-blue-700 border-blue-200', dark: 'bg-blue-500/20 text-blue-300 border-blue-500/30' },
  linear: { light: 'bg-indigo-100 text-indigo-700 border-indigo-200', dark: 'bg-indigo-500/20 text-indigo-300 border-indigo-500/30' },
  trello: { light: 'bg-sky-100 text-sky-700 border-sky-200', dark: 'bg-sky-500/20 text-sky-300 border-sky-500/30' },
  git: { light: 'bg-purple-100 text-purple-700 border-purple-200', dark: 'bg-purple-500/20 text-purple-300 border-purple-500/30' },
  confluence: { light: 'bg-cyan-100 text-cyan-700 border-cyan-200', dark: 'bg-cyan-500/20 text-cyan-300 border-cyan-500/30' },
  slack: { light: 'bg-rose-100 text-rose-700 border-rose-200', dark: 'bg-rose-500/20 text-rose-300 border-rose-500/30' },
  teams: { light: 'bg-violet-100 text-violet-700 border-violet-200', dark: 'bg-violet-500/20 text-violet-300 border-violet-500/30' },
};

export interface AnalysisSnapshotMeta {
  id: string;
  mode: AnalysisMode;
  title: string;
  timestamp: string;
  durationMs: number;
  success: boolean;
  error?: string;
  summary: {
    commitCount: number;
    eventCount: number;
    authorCount: number;
    repoCount?: number;
    dateRange?: { start: string; end: string };
    extra?: Record<string, string | number>;
  };
}

// The full data blob stored alongside the meta
// Actual shape depends on mode — stored as compressed JSON
export interface AnalysisSnapshotBlob {
  id: string; // same as meta id
  compressed: string; // LZ-String compressToUTF16 of JSON.stringify(data)
}

// ==================== Database ====================

const DB_NAME = 'verigit-analysis-history';
const DB_VERSION = 1;
const MAX_SNAPSHOTS = 50;

const STORES = {
  META: 'snapshot_meta',
  BLOBS: 'snapshot_blobs',
} as const;

let dbPromise: Promise<IDBPDatabase> | null = null;

function getDB(): Promise<IDBPDatabase> {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORES.META)) {
          const store = db.createObjectStore(STORES.META, { keyPath: 'id' });
          store.createIndex('timestamp', 'timestamp');
          store.createIndex('mode', 'mode');
        }
        if (!db.objectStoreNames.contains(STORES.BLOBS)) {
          db.createObjectStore(STORES.BLOBS, { keyPath: 'id' });
        }
      },
    });
  }
  return dbPromise;
}

// ==================== CRUD ====================

/** Save a new analysis snapshot (meta + compressed data blob) */
export async function saveSnapshot(meta: AnalysisSnapshotMeta, data: unknown): Promise<void> {
  const db = await getDB();
  const { compressed } = await poolCompress(JSON.stringify(data));
  const blob: AnalysisSnapshotBlob = { id: meta.id, compressed };

  const tx = db.transaction([STORES.META, STORES.BLOBS], 'readwrite');
  await tx.objectStore(STORES.META).put(meta);
  await tx.objectStore(STORES.BLOBS).put(blob);
  await tx.done;

  // Prune old snapshots beyond MAX_SNAPSHOTS
  await pruneSnapshots();
}

/** Get all snapshot metadata, newest first */
export async function getAllSnapshotMeta(): Promise<AnalysisSnapshotMeta[]> {
  const db = await getDB();
  const all = await db.getAll(STORES.META);
  return all.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
}

/** Get a single snapshot's metadata */
export async function getSnapshotMeta(id: string): Promise<AnalysisSnapshotMeta | undefined> {
  const db = await getDB();
  return db.get(STORES.META, id);
}

/** Load and decompress a snapshot's full data */
export async function loadSnapshotData<T = unknown>(id: string): Promise<T | null> {
  const db = await getDB();
  const blob: AnalysisSnapshotBlob | undefined = await db.get(STORES.BLOBS, id);
  if (!blob?.compressed) return null;
  try {
    const json = await poolDecompress(blob.compressed);
    return JSON.parse(json) as T;
  } catch {
    console.error('[analysis-history] Failed to decompress snapshot', id);
    return null;
  }
}

/** Delete a single snapshot */
export async function deleteSnapshot(id: string): Promise<void> {
  const db = await getDB();
  const tx = db.transaction([STORES.META, STORES.BLOBS], 'readwrite');
  await tx.objectStore(STORES.META).delete(id);
  await tx.objectStore(STORES.BLOBS).delete(id);
  await tx.done;
}

/** Clear all snapshots */
export async function clearAllSnapshots(): Promise<void> {
  const db = await getDB();
  const tx = db.transaction([STORES.META, STORES.BLOBS], 'readwrite');
  await tx.objectStore(STORES.META).clear();
  await tx.objectStore(STORES.BLOBS).clear();
  await tx.done;
}

/** Keep only the newest MAX_SNAPSHOTS entries */
async function pruneSnapshots(): Promise<void> {
  const all = await getAllSnapshotMeta();
  if (all.length <= MAX_SNAPSHOTS) return;
  const toDelete = all.slice(MAX_SNAPSHOTS);
  const db = await getDB();
  const tx = db.transaction([STORES.META, STORES.BLOBS], 'readwrite');
  for (const entry of toDelete) {
    await tx.objectStore(STORES.META).delete(entry.id);
    await tx.objectStore(STORES.BLOBS).delete(entry.id);
  }
  await tx.done;
}

// ==================== Bulk Export / Import (for profile transfer) ====================

export interface AnalysisHistoryExport {
  metas: AnalysisSnapshotMeta[];
  blobs: AnalysisSnapshotBlob[];
}

export async function exportAll(): Promise<AnalysisHistoryExport> {
  const db = await getDB();
  const [metas, blobs] = await Promise.all([
    db.getAll(STORES.META),
    db.getAll(STORES.BLOBS),
  ]);
  return { metas, blobs };
}

export async function importAll(data: AnalysisHistoryExport): Promise<{ snapshots: number }> {
  const db = await getDB();
  let count = 0;

  if (data.metas?.length || data.blobs?.length) {
    const tx = db.transaction([STORES.META, STORES.BLOBS], 'readwrite');
    for (const meta of data.metas || []) {
      await tx.objectStore(STORES.META).put(meta);
      count++;
    }
    for (const blob of data.blobs || []) {
      await tx.objectStore(STORES.BLOBS).put(blob);
    }
    await tx.done;
  }

  return { snapshots: count };
}

// ==================== Helper to generate snapshot ID ====================

export function generateSnapshotId(): string {
  return `snap-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}
